import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = "/api";

const toneStyle = {
  info: { background: "#dbeafe", color: "#1d4ed8" },
  success: { background: "#dcfce7", color: "#166534" },
  warning: { background: "#fef3c7", color: "#92400e" },
  danger: { background: "#fee2e2", color: "#991b1b" },
};

export default function NotificationBell({ actorType = "admin", actorId = 0, actorName = "", onNotificationClick }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const wrapRef = useRef(null);

  const storageKey = useMemo(() => `notification_read_at_${actorType}_${actorId || 0}`, [actorId, actorType]);

  const loadNotifications = useCallback(async () => {
    const readAfter = localStorage.getItem(storageKey) || "";
    const res = await axios.get(`${API}/notifications`, {
      params: {
        actor_type: actorType,
        actor_id: actorId || 0,
        actor_name: actorName || "",
        read_after: readAfter,
        _ts: Date.now(),
      },
    });
    setItems(Array.isArray(res.data?.items) ? res.data.items : []);
    setCount(Number(res.data?.count || 0));
  }, [actorId, actorName, actorType, storageKey]);

  useEffect(() => {
    loadNotifications().catch(() => {
      setItems([]);
      setCount(0);
    });
    const timer = window.setInterval(() => loadNotifications().catch(() => {}), 30000);
    return () => window.clearInterval(timer);
  }, [loadNotifications]);

  useEffect(() => {
    const handleClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const toggleOpen = () => {
    setOpen((prev) => !prev);
    if (!open) {
      localStorage.setItem(storageKey, new Date().toISOString());
      setCount(0);
    }
  };

  const openNotification = (item) => {
    localStorage.setItem(storageKey, new Date().toISOString());
    setCount(0);
    setOpen(false);

    if (item.targetPath) {
      navigate(item.targetPath);
      return;
    }

    if (typeof onNotificationClick === "function") {
      onNotificationClick(item);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={toggleOpen}
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "999px",
          border: "1px solid rgba(148, 163, 184, 0.18)",
          background: "rgba(255,255,255,0.72)",
          color: "#7c2d3a",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "18px",
          position: "relative",
        }}
        aria-label="Notifications"
      >
        <i className="bi bi-bell"></i>
        {count > 0 ? (
          <span
            style={{
              position: "absolute",
              top: "-6px",
              right: "-8px",
              background: "#c2410c",
              color: "#fff",
              fontSize: "10px",
              borderRadius: "999px",
              padding: "2px 6px",
              fontWeight: 800,
              minWidth: "20px",
            }}
          >
            {count > 99 ? "99+" : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          style={{
            position: "absolute",
            top: "54px",
            right: 0,
            width: "min(340px, calc(100vw - 28px))",
            background: "#fff",
            borderRadius: "18px",
            boxShadow: "0 22px 50px rgba(15, 23, 42, 0.18)",
            border: "1px solid rgba(148, 163, 184, 0.16)",
            padding: "14px",
            maxHeight: "330px",
            overflowY: "auto",
            zIndex: 2500,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
            <strong style={{ color: "#0f172a" }}>Notifications</strong>
            <button type="button" className="btn btn-sm btn-light" onClick={() => loadNotifications().catch(() => {})}>
              <i className="bi bi-arrow-clockwise"></i>
            </button>
          </div>
          {items.length > 0 ? (
            items.map((item) => {
              const tone = toneStyle[item.tone] || toneStyle.info;
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => openNotification(item)}
                  style={{ width: "100%", display: "flex", gap: "10px", alignItems: "center", padding: "10px 0", border: "none", borderBottom: "1px solid #e2e8f0", background: "transparent", textAlign: "left" }}
                >
                  <span style={{ width: "36px", height: "36px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", ...tone }}>
                    <i className={`bi ${item.icon || "bi-bell"}`}></i>
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ display: "block", color: "#0f172a", fontSize: "13px" }}>{item.label}</strong>
                    <small style={{ color: "#64748b" }}>{item.count} new update{Number(item.count) > 1 ? "s" : ""}</small>
                  </span>
                  <span style={{ ...tone, borderRadius: "999px", padding: "4px 8px", fontSize: "12px", fontWeight: 900 }}>{item.count}</span>
                </button>
              );
            })
          ) : (
            <p style={{ fontSize: "12px", color: "#64748b", margin: 0 }}>No new notifications</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
